import React, { useCallback, useMemo, useRef } from "react";
import { Box, Typography } from "@mui/material";
import {
  Location as LocationType,
  SpecialLocation,
  BilletGroup,
  GroupTotal,
  LocationTotal,
} from "../../../models";
import {
  getTotalBilletsCount,
  groupBilletsForDisplay,
  sortHeatsByPosition,
} from "../../../utils";
import { createLocationStyles, HeatGroup } from "./";
import { useTheme } from "@mui/material/styles";

interface LocationProps {
  location: LocationType | SpecialLocation;
  groups: BilletGroup[];
  isSelected?: boolean; // Visual selection state (yellow background like legacy)
  isSearchHighlighted?: boolean; // Search result highlighting
  searchMatchingGroupIds?: number[]; // Specific groups that match search
  groupTotalsByGroupName?: Record<string, GroupTotal>; // Totals per group name
  locationTotal?: LocationTotal; // Total for this location
  onClick?: () => void;
  onHoldTap?: () => void; // Hold tap callback for clear functionality
}

export const Location = React.memo(
  ({
    location,
    groups,
    isSelected = false,
    isSearchHighlighted = false,
    searchMatchingGroupIds = [],
    groupTotalsByGroupName = {},
    locationTotal,
    onClick,
    onHoldTap,
  }: LocationProps) => {
    const theme = useTheme();

    // Hold tap timer
    const holdTapTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const isHoldTapTriggeredRef = useRef<boolean>(false);
    const activePointersRef = useRef<Set<number>>(new Set());
    const pointerStartRef = useRef<{ x: number; y: number } | null>(null);

    // Memoize expensive calculations
    const totalBillets = useMemo(() => getTotalBilletsCount(groups), [groups]);

    // Memoize styles to prevent unnecessary recalculations
    const styles = useMemo(
      () =>
        createLocationStyles(
          isSelected,
          isSearchHighlighted,
          totalBillets,
          theme,
        ),
      [isSelected, isSearchHighlighted, totalBillets, theme],
    );

    // Memoize grouped and sorted data using centralized grouping logic
    const groupedAndSortedData = useMemo(() => {
      if (groups.length === 0) return { groupedByHeat: {}, sortedHeats: [] };

      // Use centralized grouping logic that handles consecutive grouping correctly
      const groupedByHeat = groupBilletsForDisplay(groups, location);
      const sortedHeats = sortHeatsByPosition(groupedByHeat);

      return { groupedByHeat, sortedHeats };
    }, [groups, location]);

    const clearHoldTimer = useCallback(() => {
      if (holdTapTimerRef.current) {
        clearTimeout(holdTapTimerRef.current);
        holdTapTimerRef.current = null;
      }
    }, []);

    // Hold tap detection - 700ms timer
    const handlePointerDown = useCallback(
      (e: React.PointerEvent) => {
        if (!onHoldTap) return;

        activePointersRef.current.add(e.pointerId);
        // A second finger (pinch/zoom) aborts any pending hold tap
        if (activePointersRef.current.size > 1) {
          clearHoldTimer();
          return;
        }

        pointerStartRef.current = { x: e.clientX, y: e.clientY };
        isHoldTapTriggeredRef.current = false;
        holdTapTimerRef.current = setTimeout(() => {
          isHoldTapTriggeredRef.current = true;
          onHoldTap();
        }, 700);
      },
      [onHoldTap, clearHoldTimer],
    );

    // Cancel the hold if the finger moves (scroll / drag / zoom)
    const handlePointerMove = useCallback(
      (e: React.PointerEvent) => {
        if (!holdTapTimerRef.current || !pointerStartRef.current) return;
        const dx = Math.abs(e.clientX - pointerStartRef.current.x);
        const dy = Math.abs(e.clientY - pointerStartRef.current.y);
        if (dx > 10 || dy > 10) clearHoldTimer();
      },
      [clearHoldTimer],
    );

    const handlePointerUp = useCallback(
      (e: React.PointerEvent) => {
        activePointersRef.current.delete(e.pointerId);
        pointerStartRef.current = null;
        clearHoldTimer();
      },
      [clearHoldTimer],
    );

    // Click handler - only trigger if hold tap wasn't triggered
    const handleClick = useCallback(
      (e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();

        // Don't trigger click if hold tap was triggered
        if (isHoldTapTriggeredRef.current) {
          isHoldTapTriggeredRef.current = false;
          return;
        }

        if (onClick) {
          onClick();
        }
      },
      [onClick],
    );

    // Optimized keyboard handler
    const handleKeyDown = useCallback(
      (e: React.KeyboardEvent) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          if (onClick) {
            onClick();
          }
        }
      },
      [onClick],
    );

    return (
      <Box
        component="div"
        sx={{
          ...styles.container,
          cursor: onClick ? "pointer" : "default",
          userSelect: "none",
          WebkitUserSelect: "none",
          MozUserSelect: "none",
          msUserSelect: "none",
        }}
        onClick={handleClick}
        onPointerDown={onHoldTap ? handlePointerDown : undefined}
        onPointerMove={onHoldTap ? handlePointerMove : undefined}
        onPointerUp={onHoldTap ? handlePointerUp : undefined}
        onPointerLeave={onHoldTap ? handlePointerUp : undefined}
        onPointerCancel={onHoldTap ? handlePointerUp : undefined}
        role="button"
        tabIndex={onClick ? 0 : -1}
        aria-label={`Location ${location.HeaderText} with ${totalBillets} billets`}
        onKeyDown={onClick ? handleKeyDown : undefined}
      >
        {/* Location Header with Total - ALWAYS VISIBLE */}
        <div style={styles.header}>
          {location.HeaderText} {totalBillets > 0 && `(${totalBillets})`}
        </div>

        {/* Billet Groups Container - NO SCROLL MIDDLE */}
        <div style={styles.content}>
          {groups.length > 0 ? (
            <>
              {groupedAndSortedData.sortedHeats.map((heatNumber) => {
                const heatGroups =
                  groupedAndSortedData.groupedByHeat[heatNumber];
                const groupName = heatGroups[0]?.GroupName;

                // Calculate totals from the actual groups in this visual group
                // This prevents double-counting when the same heat is split across non-consecutive positions
                const totalBillets = heatGroups.reduce(
                  (sum, group) => sum + group.BilletsCount,
                  0,
                );
                const totalWeight = heatGroups.reduce(
                  (sum, group) => sum + group.Weight,
                  0,
                );

                const groupTotal =
                  heatGroups.length > 0
                    ? {
                        LocationID: heatGroups[0]?.LocationID || 0,
                        GroupName: groupName || "",
                        HeatID: 0, // Not used in display
                        TotalBillets: totalBillets,
                        TotalWeight: totalWeight,
                      }
                    : undefined;

                return (
                  <HeatGroup
                    key={heatNumber}
                    heatNumber={heatNumber}
                    groups={heatGroups}
                    searchMatchingGroupIds={searchMatchingGroupIds}
                    groupTotal={groupTotal}
                  />
                );
              })}

              {/* Location Total Row - Shows total tons and billets for this location */}
              {locationTotal && (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    backgroundColor: (theme) => theme.palette.grey[300],
                    padding: "4px 6px",
                    marginTop: "4px",
                    borderRadius: "3px",
                  }}
                >
                  <Typography
                    component="span"
                    sx={{
                      fontSize: "13px",
                      fontWeight: 700,
                    }}
                  >
                    {locationTotal.TotalWeight.toFixed(1)} tons
                  </Typography>
                  <Typography
                    component="span"
                    sx={{
                      fontSize: "13px",
                      fontWeight: 700,
                      minWidth: "30px",
                      textAlign: "right",
                    }}
                  >
                    {locationTotal.TotalBillets}
                  </Typography>
                </Box>
              )}
            </>
          ) : (
            <div style={styles.emptyState}>No billets</div>
          )}
        </div>
      </Box>
    );
  },
);
